import { useGetInteractionsMultiAggregates } from '@/pages/agents/api/insights';
import { useEnableNebulySettings } from '@/pages/agents/hooks/useNebulySettings';
import styled from '@emotion/styled';
import { useAgentInsightsContext } from '../../contex/useAgentInsightsContext';
import { ProcessedInsightsUserTopicsItem, processInsightsUserTopics } from '../../utils';
import { InsightsColumn, Table, TableColumnsHeader, TableFilter, TableHeader, TableNoData } from '../Table';
import DashboardIcon from './icons/Dashboard.svg';
import LayersIcon from './icons/Layers.svg';
import LightbulbIcon from './icons/LightbulbMinimalistic.svg';
import PieGraphIcon from './icons/PieGraph.svg';
import SmileCircleIcon from './icons/SmileCircle.svg';
import UsersCircleIcon from './icons/UsersCircle.svg';
import { TopicRow } from './TopicRow';
import { TopicRowSkeleton } from './TopicRowSkeleton';
import { TopicTableUpgrade } from './TopicTableUpgrade';

const FilterContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: 12px;
`;

export const topicTableColumns: InsightsColumn[] = [
    { label: 'Topic', icon: LayersIcon, grow: 2.5 },
    { label: 'Intent', icon: LightbulbIcon, grow: 3 },
    {
        label: 'Satisfaction',
        icon: SmileCircleIcon,
        grow: 2,
        tooltip: "Signals extracted from the conversation indicating how satisfied the user is with the Agent's response",
    },
    { label: 'Users', icon: UsersCircleIcon, grow: 1.5 },
    { label: 'Conversations', icon: DashboardIcon, grow: 1.5 },
    { label: 'Total', icon: PieGraphIcon, minWidth: 60 },
];

export const TopicsTable = () => {
    const {
        topicsFilter,
        setTopicsFilter,
        intentsFilter,
        setIntentsFilter,
        dateRange,
        from,
        to,
        agent_ids,
        shareId,
        isPublic,
        setConversationTopicFilter,
        setConversationIntentFilter,
    } = useAgentInsightsContext();

    // On public pages, insights are enabled if they have a share link
    // On private pages, check the settings
    const { isEnabled } = useEnableNebulySettings(!isPublic);
    const isAgentInsightsEnabled = (isPublic || isEnabled) ?? undefined;

    const { data, isLoading } = useGetInteractionsMultiAggregates(
        {
            dateRange,
            from,
            to,
            agent_ids,
            shareId,
            isPublic,
            group_by_groups: [['conversation_topic', 'conversation_user_action']],
            variables: ['conversation_user_feedback', 'n_sessions'],
        },
        !!isAgentInsightsEnabled
    );
    const { topics, intents, items } = processInsightsUserTopics(data);
    const filteredItems = items.filter(
        item =>
            (topicsFilter.length === 0 || topicsFilter.includes(item.topic)) &&
            (intentsFilter.length === 0 || intentsFilter.includes(item.intent))
    );

    const handleRowClick = (item: ProcessedInsightsUserTopicsItem) => {
        setConversationTopicFilter(item.topic);
        setConversationIntentFilter(item.intent);
    };

    if (isAgentInsightsEnabled === undefined) {
        return null;
    }

    return (
        <Table>
            <TableHeader
                title="User topics"
                actions={
                    <FilterContainer>
                        <TableFilter label="Topics" options={topics} value={topicsFilter} onSubmit={setTopicsFilter} />
                        <TableFilter label="Intents" options={intents} value={intentsFilter} onSubmit={setIntentsFilter} />
                    </FilterContainer>
                }
            />
            <TableColumnsHeader columns={topicTableColumns} />
            {/* not enabled */}
            {!isAgentInsightsEnabled && <TopicTableUpgrade />}
            {/* loading */}
            {!!isAgentInsightsEnabled && isLoading && (
                <>
                    <TopicRowSkeleton columns={topicTableColumns} sizes={[100, 100, 50, 50, 50, 50]} />
                    <TopicRowSkeleton columns={topicTableColumns} sizes={[70, 70, 30, 30, 30, 30]} />
                </>
            )}
            {/* display data */}
            {!!isAgentInsightsEnabled &&
                !isLoading &&
                filteredItems.map(item => (
                    <TopicRow
                        key={`${item.topic}-${item.intent}`}
                        columns={topicTableColumns}
                        item={item}
                        onClick={() => handleRowClick(item)}
                    />
                ))}
            {/* no data available */}
            {!!isAgentInsightsEnabled && !isLoading && !filteredItems.length && <TableNoData />}
        </Table>
    );
};
